<script>
  import { onDestroy } from 'svelte'
  import { router } from '@inertiajs/svelte'
  import HierarchyPath from '../HierarchyPath/index.svelte'
  import StatusBadge from '../StatusBadge.svelte'
  import DeleteConfirmation from '../DeleteConfirmation.svelte'
  import MoveItemModal from './../MoveItemModal/index.svelte'

  // Cross-property search for the dashboard. Same result UI as PropertySearch,
  // but hits the global /dashboard/search endpoint and uses each result's own
  // property_id for navigation (results span multiple properties).
  let searchTerm = $state('')
  let results = $state([])
  let isLoading = $state(false)
  let page = $state(1)
  let pages = $state(0)
  let hasMore = $state(false)
  let scrollContainer = $state()
  let timeout

  function debounce() {
    clearTimeout(timeout)
    timeout = setTimeout(() => performSearch(1), 300)
  }

  async function performSearch(p = 1, append = false) {
    if (isLoading) return
    isLoading = true
    try {
      const res = await fetch(
        `/dashboard/search?search_term=${encodeURIComponent(searchTerm)}&page=${p}`,
        { headers: { 'X-Requested-With': 'XMLHttpRequest', 'Accept': 'application/json' } }
      )
      if (!res.ok) throw new Error('Search failed')
      const data = await res.json()
      results = append ? [...results, ...data.results] : data.results
      page = data.pagy.page
      pages = data.pagy.pages
      hasMore = data.pagy.page < data.pagy.pages
    } catch (e) {
      console.error('Search error:', e)
    } finally {
      isLoading = false
    }
  }

  function handleScroll() {
    if (!scrollContainer || isLoading || !hasMore) return
    const { scrollTop, scrollHeight, clientHeight } = scrollContainer
    if (scrollTop + clientHeight >= scrollHeight - 50) performSearch(page + 1, true)
  }

  function clearSearch() {
    searchTerm = ''
    results = []
  }

  // Move + delete on search results. Results span items and containers; the
  // move modal/endpoint are shared (a container is an item), and delete branches
  // on type so containers use their own flow (asking what to do with contents).
  let resultToMove = $state(null)
  let resultToDelete = $state(null)
  let deleteContainerItems = $state(false)
  let deleteContainerChildren = $state(false)

  const deletingContainer = $derived(resultToDelete?.type === 'container')

  // The move modal reads container_type to know it's a container; search items
  // don't carry it, so synthesize the shape it expects from the result.
  function moveTarget(result) {
    return {
      id: result.id,
      name: result.name,
      property_id: result.property_id,
      space_id: result.space_id,
      container_id: result.container_id || '',
      container_type: result.type === 'container' ? (result.container_type || 'box') : null,
      item_count: result.item_count,
      child_container_count: result.child_container_count
    }
  }

  function handleMove(result) {
    resultToMove = moveTarget(result)
  }

  function handleDeleteClick(result) {
    resultToDelete = result
    deleteContainerItems = false
    deleteContainerChildren = false
  }

  function refreshAfterChange() {
    // Re-run the current search so the list reflects the move/delete.
    if (searchTerm.trim()) performSearch(1)
  }

  function handleDeleteConfirm({ checkboxChecked } = {}) {
    if (!resultToDelete) return
    const target = resultToDelete
    const opts = { preserveScroll: true, onFinish: refreshAfterChange }
    if (target.type === 'container') {
      router.delete(`/containers/${target.id}`, {
        data: { delete_items: checkboxChecked, delete_children: deleteContainerChildren },
        ...opts
      })
    } else {
      router.delete(`/properties/${target.property_id}/items/${target.id}`, opts)
    }
    resultToDelete = null
  }

  function handleDeleteCancel() {
    resultToDelete = null
    deleteContainerItems = false
    deleteContainerChildren = false
  }

  function itemHref(result) {
    return result.type === 'container'
      ? `/containers/${result.id}`
      : `/properties/${result.property_id}/items/${result.id}`
  }

  function editHref(result) {
    return result.type === 'container'
      ? `/containers/${result.id}/edit`
      : `/properties/${result.property_id}/items/${result.id}/edit`
  }

  function navigate(result, e) {
    if (e) e.preventDefault()
    router.visit(itemHref(result))
  }

  function formatContainerType(type) {
    if (!type) return 'Container'
    return type.split('_').map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')
  }

  onDestroy(() => clearTimeout(timeout))
</script>

<div class="relative">
  <label class="flex items-center gap-2.5 bg-white border border-gray-200 rounded-xl px-4 py-3 shadow-sm focus-within:border-brand-600 focus-within:ring-2 focus-within:ring-brand-100 transition-all">
    <svg class="h-5 w-5 flex-shrink-0 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/></svg>
    <input
      type="text"
      bind:value={searchTerm}
      oninput={debounce}
      placeholder="Search items and containers across all your properties…"
      class="flex-1 border-none bg-transparent p-0 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-0"
    />
    {#if searchTerm}
      <button onclick={clearSearch} aria-label="Clear search" class="flex-shrink-0 text-gray-400 hover:text-gray-600">
        <svg class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path d="M6 18L18 6M6 6l12 12" /></svg>
      </button>
    {/if}
  </label>

  {#if searchTerm.trim()}
    <div class="mt-3 bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
      {#if results.length > 0}
        <div class="px-5 py-3 border-b border-gray-100 text-xs text-gray-500">
          Showing {results.length} results for “{searchTerm}”{#if pages > 1} (page {page} of {pages}){/if}
        </div>

        <div bind:this={scrollContainer} onscroll={handleScroll} class="max-h-96 overflow-y-auto px-2 py-1.5">
          <ul class="divide-y divide-gray-100">
            {#each results as result (result.type + result.id)}
              <li class="group relative flex items-center gap-3.5 px-3 py-2.5 rounded-xl hover:bg-gray-50">
                <button class="absolute inset-0 w-full h-full cursor-pointer bg-transparent border-none z-10" onclick={(e) => navigate(result, e)} aria-label="View {result.name}"></button>

                {#if result.image_url}
                  <img src={result.image_url} alt={result.name} class="h-13 w-13 flex-shrink-0 object-cover rounded-lg bg-gray-100" />
                {:else}
                  <div class="h-13 w-13 flex-shrink-0 rounded-lg {result.type === 'container' ? 'bg-amber-50 text-amber-700' : 'bg-brand-50 text-brand-700'} grid place-items-center">
                    {#if result.type === 'container'}
                      <svg class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path d="M21 8l-9-5-9 5v8l9 5 9-5V8z"/><path d="M3 8l9 5 9-5"/></svg>
                    {:else}
                      <svg class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"/></svg>
                    {/if}
                  </div>
                {/if}

                <div class="flex-1 min-w-0">
                  <div class="flex items-center gap-2 flex-wrap">
                    <h3 class="text-sm font-semibold text-gray-900 truncate">{result.name}</h3>
                    <span class="text-[11px] font-semibold rounded-full px-2 py-0.5 {result.type === 'container' ? 'bg-amber-100 text-amber-800' : 'bg-brand-100 text-brand-800'}">
                      {result.type === 'container' ? 'Container' : 'Item'}
                    </span>
                    <StatusBadge value={result.status} />
                  </div>
                  {#if result.description}
                    <p class="text-xs text-gray-500 truncate">{result.description}</p>
                  {/if}
                  <div class="mt-1">
                    <HierarchyPath path={result.hierarchy_path} highlightLast={false} />
                  </div>
                  <div class="flex flex-wrap items-center gap-2.5 mt-1 text-xs text-gray-400">
                    {#if result.type === 'container'}
                      <span>Type: {formatContainerType(result.container_type)}</span>
                      {#if result.item_count > 0}<span>{result.item_count} items</span>{/if}
                    {:else}
                      {#if result.remaining_quantity > 1}<span>Qty: {result.remaining_quantity}</span>{/if}
                      {#if result.low_stock}
                        <span class="inline-flex items-center gap-1 text-amber-700 font-semibold"><svg class="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z"/><path d="M12 9v4"/><path d="M12 17h.01"/></svg>Low</span>
                      {/if}
                    {/if}
                    {#if result.is_valuable}
                      <svg class="h-3.5 w-3.5 text-amber-500 inline-block" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-label="Valuable"><title>Valuable</title><path d="M6 3h12l3 6-9 12L3 9l3-6z"/><path d="M3 9h18"/><path d="M9 3l3 6 3-6"/><path d="M12 9v12"/></svg>
                    {/if}
                    {#if result.is_favorite}
                      <svg class="h-3.5 w-3.5 text-red-500 inline-block" viewBox="0 0 24 24" fill="currentColor" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-label="Favorite"><title>Favorite</title><path d="M12 20s-7-4.5-9.5-9A5 5 0 0112 5a5 5 0 019.5 6c-2.5 4.5-9.5 9-9.5 9z"/></svg>
                    {/if}
                  </div>
                </div>

                <div class="relative z-20 flex-shrink-0 flex items-center gap-1">
                  <button
                    onclick={(e) => { e.stopPropagation(); handleMove(result) }}
                    class="p-2 rounded-lg border border-gray-200 bg-white text-gray-400 hover:text-brand-700 hover:border-brand-600 transition-colors"
                    title="Move {result.name}"
                    aria-label="Move {result.name}"
                  >
                    <svg class="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M4 17h9a4 4 0 004-4V7"/><path d="M14 7l3-3 3 3"/></svg>
                  </button>
                  <a
                    href={editHref(result)}
                    onclick={(e) => e.stopPropagation()}
                    class="p-2 rounded-lg border border-gray-200 bg-white text-gray-400 hover:text-brand-700 hover:border-brand-600 transition-colors"
                    title="Edit {result.name}"
                    aria-label="Edit {result.name}"
                  >
                    <svg class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
                  </a>
                  <button
                    onclick={(e) => { e.stopPropagation(); handleDeleteClick(result) }}
                    class="p-2 rounded-lg border border-gray-200 bg-white text-gray-400 hover:text-red-600 hover:border-red-400 hover:bg-red-50 transition-colors"
                    title="Delete {result.name}"
                    aria-label="Delete {result.name}"
                  >
                    <svg class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                </div>
              </li>
            {/each}
          </ul>

          {#if isLoading && results.length > 0}
            <div class="flex items-center justify-center gap-2 py-3 text-xs text-gray-400">
              <span class="h-3.5 w-3.5 rounded-full border-2 border-gray-200 border-t-brand-600 animate-spin"></span>
              Loading more results…
            </div>
          {/if}
          {#if !hasMore && results.length > 0 && !isLoading}
            <div class="text-center py-3 text-xs text-gray-400">End of results</div>
          {/if}
        </div>
      {:else if !isLoading}
        <div class="text-center py-8 px-5 text-sm text-gray-500">No items or containers match “{searchTerm}”.</div>
      {:else}
        <div class="flex items-center justify-center gap-2 py-8 text-xs text-gray-400">
          <span class="h-3.5 w-3.5 rounded-full border-2 border-gray-200 border-t-brand-600 animate-spin"></span>
          Searching…
        </div>
      {/if}
    </div>
  {/if}
</div>

<!-- Delete: containers get the richer flow (choose what happens to contents);
     items get a plain confirmation. -->
{#if deletingContainer}
  <DeleteConfirmation
    show={!!resultToDelete}
    title="Delete Container"
    message="Are you sure you want to delete this container? This action cannot be undone."
    checkboxLabel="Delete all items in this container"
    bind:checkboxChecked={deleteContainerItems}
    onConfirm={handleDeleteConfirm}
    onCancel={handleDeleteCancel}
  >
    {#snippet additionalOptions()}
      <div class="flex items-center">
        <label class="flex items-center">
          <input
            type="checkbox"
            bind:checked={deleteContainerChildren}
            class="h-4 w-4 rounded border-gray-300 text-brand-700 focus:ring-brand-600"
          />
          <span class="ml-2 text-sm text-gray-700">Delete all child containers and their items</span>
        </label>
      </div>
    {/snippet}
  </DeleteConfirmation>
{:else}
  <DeleteConfirmation
    show={!!resultToDelete}
    title="Delete Item"
    message="Are you sure you want to delete this item? This action cannot be undone and will remove any associated images."
    onConfirm={handleDeleteConfirm}
    onCancel={handleDeleteCancel}
  />
{/if}

<MoveItemModal open={!!resultToMove} item={resultToMove} onClose={() => (resultToMove = null)} />
